import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Calendar, DatePicker, TimePicker } from "@progress/kendo-react-dateinputs";
import { format, parseISO } from "date-fns";
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { setCommonGridReminders } from "../../../actions/reminders";
import { icon, label, number, quote } from "../../../config";
import { REMINDER_DUE_DATE_OPTIONS, button, dateFormat } from "../../../config/constants";
import { convertToTimeZone, formatReminderTimeInTimeZone, formatTime, getCombinedReminderUtcDate, parseTimeStringToDate } from "../../../helper/common";
import { subStr } from "../../../shared/validators/validator";
import store from "../../../store";
import { dateConvertUTC } from "../../../utils";
import { handleContentUpdate } from "../../ContentManagementSystem/helper";
import { planMyDayReminderActions } from "../../TaskSidebar/PlanMyDayActions/planMyDay.helper";
import { handleTaskKeyUpdate } from "../tasks.service";
import { ReminderContext } from "./ReminderContextProvider";
import "./reminder.scss";
import { addReminder, deleteReminder } from "./reminder.service";
import { Tooltip } from "@progress/kendo-react-tooltip";

export const useReminderList = (handleOutSideClick) => {
  const dispatch = useDispatch();
  const { task } = useSelector((state) => state.taskSidebar);
  const {
    setEditRem,
    setEditName,
    setEditDate,
    setEditTime,
    setIsDynamic,
    setDelId,
    setEditAddRem,
    setIsUpdatingReminder,
    editAddRem,
    setIsAddClicked,
    setIsReminderClicked,
  } = useContext(ReminderContext);
  const { contentManagerDetailsData } = useSelector((state) => state.contentManager);
  const { reminders } = useSelector((state) => state.reminders);
  const { projectId } = useParams();

  /**
   * show hide modal/popup — sets edit date/time in user timezone (same as ReminderGridCells).
   */
  const handleClick = useCallback(
    ({ Id, ReminderMessage, ReminderDate, ReminderTime, ReminderType }) => {
      const combinedReminderUtc = getCombinedReminderUtcDate(ReminderDate, ReminderTime);
      const editDateStr = combinedReminderUtc ? convertToTimeZone(combinedReminderUtc, "dd MMM yy") : ReminderDate  ? convertToTimeZone(ReminderDate, "dd MMM yy")  : format(parseISO(ReminderDate), dateFormat.REMINDER_DATE_FORMAT);
      const editTimeStr = formatReminderTimeInTimeZone(ReminderDate, ReminderTime, "HH:mm:ss") ?? ReminderTime ?? "";
      setEditName(ReminderMessage);
      setEditDate(editDateStr);
      setEditTime(editTimeStr);
      setIsDynamic(ReminderType === number.TWO);
      setEditRem(Id);
      setEditAddRem(!editAddRem);
      setIsReminderClicked(true);
      setIsUpdatingReminder(true);
    },
    [editAddRem]
  );

  /**
   * handle api for reminder in case of task and library attachments
   * @param {payload(reminderId  )}
   * @returns {Void}
   */
  const delReminder = async (Id, e) => {
    e.stopPropagation();
    const payload = {
      reminderId: Id,
    };
    dispatch(deleteReminder(payload));
    if (task.taskId) {
      const remainingReminderCount = task.reminderCount - number.ONE;
      handleTaskKeyUpdate(false, "reminderCount", remainingReminderCount);
      remainingReminderCount === number.ZERO && handleOutSideClick();
    } else if (reminders?.length === number.ONE && contentManagerDetailsData?.id) {
      const updatedField = { hasReminder: false };
      handleContentUpdate(updatedField, projectId);
      handleOutSideClick();
    }
    setDelId(null);
  };

  /**
   * Sets the Id of reminder that is meant to be deleted
   * @param {payload(reminderId  )}
   * @returns {Void}
   */
  const setDelReminder = (Id, e) => {
    e.stopPropagation();
    setDelId(Id);
  };

  /**
   * handle Add Reminders
   */
  const handleAddRem = (fromAddButton) => {
    if (fromAddButton) setIsAddClicked(true);
    else setIsReminderClicked(false);
    setIsUpdatingReminder(false);
    setEditRem(null);
    setEditAddRem(!editAddRem);
  };

  return { handleClick, delReminder, setDelReminder, handleAddRem };
};

export const useDynamicReminderPopup = ({ setShowReminder, reminderType, commentText, entityId, entityType }) => {
  const {
    onDueDate,
    setOnDueDate,
    daysBeforeChecked,
    setDaysBeforeChecked,
    daysAfterChecked,
    setDaysAfterChecked,
    daysBefore,
    setDaysBefore,
    daysAfter,
    setDaysAfter,
    showCustomPopup,
    setShowCustomPopup,
    reminderMessage,
    setReminderMessage,
    onTomorrow,
    setOnTomorrow,
    displayWarning,
    setDisplayWarning,
    isAddClicked,
    setIsAddClicked,
    editAddRem,
    setEditAddRem,
    isDueDatePassed,
    isReminderMessageEmpty,
    setIsReminderMessageEmpty,
    showCalendar,
    setShowCalendar,
    setIsDueDatePassed,
  } = useContext(ReminderContext);
  const { UserReminderTime } = useSelector((state) => state.auth.user);
  const { commonGridReminders } = useSelector((state) => state.reminders);
  const { task } = useSelector((state) => state.taskSidebar);
  const { user } = useSelector((state) => state.auth);
  const { contentManagerDetailsData } = useSelector((state) => state.contentManager);
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const today = new Date();
  const defaultTime = formatTime(UserReminderTime, dateFormat.REMINDER_TIME_FORMAT);
  const titleRef = useRef(null);
  const timePickerPopupHostRef = useRef(null);
  const formattedTime = formatTime(UserReminderTime, "HH:mm");
  let initialTime = parseTimeStringToDate(formattedTime);
  const [selectedTime, setSelectedTime] = useState(initialTime);
  
  // State for "Choose Date" option (replaces Tomorrow)
  const [chooseDateChecked, setChooseDateChecked] = useState(true);
  
  // Calculate default date (tomorrow)
  const getDefaultDate = () => {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  };
  const [selectedDate, setSelectedDate] = useState(getDefaultDate());

  /**
   * Adjusts time when today is selected based on business rules (all in user's profile timezone):
   * - Default time comes from user profile (UserReminderTime).
   * - If selected date is today (in user TZ) and current time (in user TZ) is after default, set to 1 hour ahead of current (user TZ).
   * - If after 11 PM (user TZ) and default has passed, set to 11:59 PM (user TZ).
   * - Prevents setting a reminder in the past.
   * @author Sejal
   */
  useEffect(() => {
    if (!chooseDateChecked || !selectedDate) return;
    const userTimezone = store.getState()?.auth?.user?.timezone || "UTC";
    const nowInUserTz = utcToZonedTime(new Date(), userTimezone);
    const todayStrUserTz = formatInTimeZone(new Date(), userTimezone, "yyyy-MM-dd");
    const selectedDateStrUserTz = formatInTimeZone(selectedDate, userTimezone, "yyyy-MM-dd");
    const isTodayInUserTz = todayStrUserTz === selectedDateStrUserTz;

    const defaultTime = parseTimeStringToDate(formattedTime || "09:00");
    const defaultHour = defaultTime.getHours();
    const defaultMinute = defaultTime.getMinutes();

    if (isTodayInUserTz) {
      const currentHour = nowInUserTz.getHours();
      const currentMinute = nowInUserTz.getMinutes();
      const currentMins = currentHour * 60 + currentMinute;
      const defaultMins = defaultHour * 60 + defaultMinute;

      if (currentMins >= defaultMins) {
        if (currentHour >= 23) {
          setSelectedTime(new Date(2000, 0, 1, 23, 59, 0, 0));
        } else {
          const oneHourAheadHour = currentHour + 1;
          const oneHourAheadMin = currentMinute;
          setSelectedTime(new Date(2000, 0, 1, oneHourAheadHour, oneHourAheadMin, 0, 0));
        }
      } else {
        setSelectedTime(new Date(2000, 0, 1, defaultHour, defaultMinute, 0, 0));
      }
    } else {
      setSelectedTime(new Date(2000, 0, 1, defaultHour, defaultMinute, 0, 0));
    }
  }, [selectedDate, chooseDateChecked, formattedTime]);

  // Memoized truncated comment text
  const truncatedCommentText = useMemo(() => {
    if (!commentText) return task?.Name || "";
    return commentText.length > 120 ? commentText.substring(0, 120) : commentText;
  }, [commentText, task?.Name]);

  /**
   * if the reminder is from comment, then choose date option should be ticked
   * @author Sarthak
   */
  useEffect(() => {
    if (reminderType === number.FIVE) {
      setChooseDateChecked(true);
    }
  }, [reminderType]);

  /**
   *  handles the increment and decrement of number of days for dynamic reminders ( future due date)
   * @param {boolean} increment - Whether increment button is pressed or decrement
   * @author Sarthak Arora
   */
  const handleDaysBeforeChange = useCallback(
    (increment) => {
      const newDaysBefore = increment ? Number(daysBefore) + number.ONE : Number(daysBefore) - number.ONE;
      
      // Prevent decrement below 1
      if (newDaysBefore < number.ONE) {
        return;
      }
      
      const calculatedDate = new Date(task.DueDate);
      calculatedDate.setDate(calculatedDate.getDate() - newDaysBefore);
      const isDateValid = calculatedDate > today;

      if (newDaysBefore >= number.ONE && isDateValid) {
        setDisplayWarning(false);
        setDaysBefore(newDaysBefore);
      } else {
        setDisplayWarning(true);
      }
    },
    [daysBefore, task.DueDate, today]
  );

  /**
   *  toggles daysBeforeChecked
   *  @author Sarthak Arora
   */

  const handleDaysBeforeChecked = () => {
    setDaysBeforeChecked(!daysBeforeChecked);
  };

  /**
   *  toggles daysAfterChecked
   * @author Sarthak Arora
   */

  const handleDaysAfterChecked = () => {
    setDaysAfterChecked(!daysAfterChecked);
  };

  /**
   *  toggles chooseDateChecked
   * @author Sarthak Arora
   */
  const handleChooseDateChecked = () => {
    setChooseDateChecked(!chooseDateChecked);
  };

  /**
   * handles DatePicker change
   * @author Assistant
   */
  const handleDateChange = (e) => {
    if (e.value) {
      const newDate = new Date(e.value);
      newDate.setHours(0, 0, 0, 0);
      setSelectedDate(newDate);
    }
  };

  /**
   * handle the cancel btn
   * @author Sarthak Arora
   */
  const handleCancel = () => {
    setShowReminder(false);
  };

  /**
   * focus on the title field
   * @author Sarthak Arora
   */
  const handleFocus = () => {
    if (titleRef.current) {
      titleRef.current.focus();
    }
  };

  /**
   *   handles the increment and decrement of number of days for normal reminders (past due date)
   * @param {boolean} increment - Whether increment button is pressed or decrement
   *
   * @author Sarthak Arora
   */

  const handleDaysAfterChange = (increment) => {
    const newDaysAfter = increment ? daysAfter + number.ONE : daysAfter - number.ONE;
    if (newDaysAfter >= number.TWO) setDaysAfter(newDaysAfter);
  };

  /**
   *   handles  the visibilty of popup for custom reminder
   * @author Sarthak Arora
   */

  const handleCustomClick = () => {
    setShowCustomPopup(true);
  };

  /**
   *   closes the custom reminder popup
   * @author Sarthak Arora
   */

  const closeCustomPopup = () => {
    setShowCustomPopup(false);
  };

  /**
   *   resets daysBefore whenever checkbox is toggled
   * @author Sarthak Arora
   */
  useEffect(() => {
    setDaysBefore(number.ONE);
  }, [daysBeforeChecked]);

  /**
   *   resets daysAfter whenever checkbox is toggled
   * @author Sarthak Arora
   */
  useEffect(() => {
    setDaysAfter(number.TWO);
  }, [daysAfterChecked]);

  /**
   * initialise IsReminderMessageEmpty
   * @author Sarthak Arora
   */
  useEffect(() => {
    setIsReminderMessageEmpty(!!!reminderMessage);
  }, []);

  /**
   * returns the updated commonGridReminders
   * @param {*} response
   * @returns {Object} updatedCommonGridReminders
   * @author Sarthak Arora
   */
  const getUpdatedCommonGridReminders = ({ Id, ReminderMessage, ReminderTime, ReminderDate, ReminderType, EntityId, IsSeen }) => {
    const newReminder = {
      Id: Id,
      ReminderMessage: ReminderMessage,
      ReminderTime: ReminderTime,
      ReminderDate: ReminderDate,
      ReminderType: ReminderType,
      ProjectTaskId: task.ProjectTaskId,
      Name: task.Name,
      DueDate: task.DueDate,
      taskId: EntityId,
      IsSeen: IsSeen ? IsSeen : 0,
      Status: new Date(ReminderDate) > new Date() ? label.UPCOMING : label.DUE,
    };
    const updatedCommonGridReminders = [...commonGridReminders, newReminder];
    return updatedCommonGridReminders;
  };

  /**
   * Creates an alert notification action.
   * @param {Object} params - Parameters for creating a reminder
   * @param {Date} params.selectedTime - Time of the reminder
   * @param {Date} params.reminderDate - Date of the reminder being created
   * @param {string} params.reminderMessage - Message of the reminder being created
   * @param {number} params.reminderType - type of reminder ( 1 for custom/normal , 2 for dynamic)
   * @param {Object} params.extraParams - Additional parameters for the reminder
   * @author Sarthak Arora
   */

  const createReminder = async ({ selectedTime, reminderDate, reminderMessage, reminderType, extraParams = {} }) => {
    const { id: mongoEntityId } = contentManagerDetailsData || {};
    const reminderDateObj = new Date(reminderDate);
    const userTimezone = store.getState()?.auth?.user?.timezone || "UTC";
    const localMoment = new Date(
      reminderDateObj.getFullYear(),
      reminderDateObj.getMonth(),
      reminderDateObj.getDate(),
      selectedTime.getHours(),
      selectedTime.getMinutes(),
      0,
      0
    );
    const utcMoment = zonedTimeToUtc(localMoment, userTimezone);
    const payload = {
      userId: user.id,
      reminderMessage: reminderType === number.FIVE ? commentText || task?.Name : reminderMessage,
      reminderTime: formatInTimeZone(utcMoment, "UTC", "HH:mm"),
      reminderDate: dateConvertUTC(utcMoment),
      reminderId: null,
      reminderType: task.taskId || entityId ? reminderType : number.FOUR,
      mongoEntityId: !task.taskId ? mongoEntityId : null,
      entityId: entityId ?? task.taskId ?? null,
      entityType: entityType ?? task.taskId ? number.FOUR : number.TWENTY_THREE,
      ...extraParams,
    };
    const res = await dispatch(addReminder(payload));
    if (res) await dispatch(setCommonGridReminders(getUpdatedCommonGridReminders(res)));
    if (contentManagerDetailsData?.id && !task.taskId) {
      const updatedField = { hasReminder: true };
      handleContentUpdate(updatedField, projectId);
    }
    return res;
  };

  /**
   *   handles the creation of reminder based on the type
   * @author Sarthak Arora
   */

  const handleSaveReminder = useCallback(async () => {
    handleCancel();
    const dueDate = new Date(task.DueDate);
    let reminderCount = 0;
    let reminderResponse;
    const reminderText = subStr(reminderMessage, 0, 120);
    if (!isDueDatePassed) {
      if (chooseDateChecked && selectedDate) {
        reminderResponse = await createReminder({
          selectedTime,
          reminderDate: selectedDate,
          reminderMessage: reminderText,
          reminderType,
        });
        if (reminderResponse) reminderCount++;
      }

      if (daysBeforeChecked) {
        const reminderDateBeforeDue = new Date(dueDate.getTime() - daysBefore * 24 * 60 * 60 * 1000);
        reminderResponse = await createReminder({
          selectedTime,
          reminderDate: reminderDateBeforeDue,
          reminderMessage,
          reminderType: number.TWO,
          extraParams: { daysBefore: daysBefore, onDueDate: false },
        });
        if (reminderResponse) reminderCount++;
      }
    } else {
      if (chooseDateChecked && selectedDate) {
        reminderResponse = await createReminder({
          selectedTime,
          reminderDate: selectedDate,
          reminderMessage: reminderText,
          reminderType,
        });
        if (reminderResponse) reminderCount++;
      }

      if (daysAfterChecked) {
        const reminderDateAfterToday = new Date(today.getTime() + daysAfter * 24 * 60 * 60 * 1000);
        reminderResponse = await createReminder({
          selectedTime,
          reminderDate: reminderDateAfterToday,
          reminderMessage: reminderText,
          reminderType,
        });
        if (reminderResponse) reminderCount++;
      }
    }

    setEditAddRem(false);
    setIsAddClicked(false);
    setShowCustomPopup(false);
    handleTaskKeyUpdate(false, "reminderCount", task.reminderCount + reminderCount);
    setDaysBefore(number.ONE);
    setDaysAfter(number.TWO);
    planMyDayReminderActions();
  }, [
    isDueDatePassed,
    chooseDateChecked,
    selectedDate,
    daysBeforeChecked,
    daysAfterChecked,
    reminderMessage,
    task.reminderCount,
    task.DueDate,
    daysBefore,
    daysAfter,
    selectedTime,
  ]);

  /**
   * change handler for TimePicker
   * @author Sarthak Arora
   */
  /**
   * handles shared TimePicker change
   * Validates time based on selected date when "Choose Date" is checked
   * @author Sejal
   */
  const handleTimeChange = (e) => {
    if (!e.target.value) return;
    const newTime = e.target.value;
    const userTimezone = store.getState()?.auth?.user?.timezone || "UTC";
    const nowInUserTz = utcToZonedTime(new Date(), userTimezone);
    const todayStrUserTz = formatInTimeZone(new Date(), userTimezone, "yyyy-MM-dd");
    const selectedDateStrUserTz = formatInTimeZone(selectedDate, userTimezone, "yyyy-MM-dd");
    const isTodayInUserTz = todayStrUserTz === selectedDateStrUserTz;

    if (chooseDateChecked && selectedDate && isTodayInUserTz) {
      const currentHour = nowInUserTz.getHours();
      const currentMinutes = nowInUserTz.getMinutes();
      const selectedHour = newTime.getHours();
      const selectedMinutes = newTime.getMinutes();
      const selectedTimeInMinutes = selectedHour * 60 + selectedMinutes;
      const currentTimeInMinutes = currentHour * 60 + currentMinutes;
      const oneHourAheadInMinutes = currentTimeInMinutes + 60;

      if (currentHour >= 23) {
        if (selectedTimeInMinutes >= currentTimeInMinutes) setSelectedTime(newTime);
      } else {
        if (selectedTimeInMinutes >= oneHourAheadInMinutes) setSelectedTime(newTime);
      }
      return;
    }
    setSelectedTime(newTime);
  };
  const removeReminderTimePickerActionTooltips = useCallback(() => {
    requestAnimationFrame(() => {
      const popupHost = timePickerPopupHostRef.current;
      if (!popupHost) return;
      popupHost.querySelectorAll(".k-time-cancel[title], .k-time-accept[title], .k-actions [title]").forEach((el) => {
        el.removeAttribute("title");
      });
    });
  }, []);

  /**
   *  sets the reminderMessage
   *  @author: Sarthak Arora
   */

  const inputEvent = (event) => {
    setIsReminderMessageEmpty(event.target.value === "");
    setReminderMessage(event.target.value);
  };

  /**
   *  displays the due date
   * @param {Date} dueDate - dueDate of the task
   *  @author: Sarthak Arora
   */

  const displayDueDate = (dueDate) => {
    if (!dueDate) return "";
    return `(${new Date(dueDate).toLocaleDateString("en-GB", REMINDER_DUE_DATE_OPTIONS).replace(/ /g, "-")})`;
  };

  /**
   *  handles the keyboard typed input for number of days (dynamic reminder)
   *  @author: Sarthak Arora
   */

  const handleNumberOfDaysBefore = useCallback(
    (e) => {
      const newDaysBefore = e.target.value;
      if (!newDaysBefore) {
        setDaysBefore(number.ONE);
        setDaysBeforeChecked(false);
      }
      const calculatedDate = new Date(task.DueDate);
      calculatedDate.setDate(calculatedDate.getDate() - newDaysBefore);
      const isDateValid = calculatedDate > today;

      if (!isDateValid) setDisplayWarning(true);
      else {
        setDisplayWarning(false);
      }
      setDaysBefore(newDaysBefore);
    },
    [task.DueDate, today]
  );

  /**
   *  handles the keyboard typed input for number of days (custom reminder)
   *  @author: Sarthak Arora
   */

  const handleNumberOfDaysAfter = (e) => {
    if (!e.target.value) {
      setDaysAfter(number.ONE);
      setDaysAfterChecked(false);
    }
    setDaysAfter(e.target.value);
  };

  /**
   *  restricts the key down to numbers only
   *  @author: Sarthak Arora
   */

  const handleKeyDown = (e) => {
    if (
      !(
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "Backspace" ||
        e.key === "Delete" ||
        e.key === "Tab" ||
        (e.key >= "0" && e.key <= "9")
      )
    ) {
      e.preventDefault();
    }
  };

  const popupBodyContent = () => {
    return (
      <div className='reminder-popup-content'>
        <Tooltip anchorElement='target' position='bottom' parentTitle={true}>
        {/* Title Section */}
        <div className='form-group'>
          <label className='mb-1'>{label.TITLE}</label>
          <textarea
            id='dynamic-reminder-popup-reminder-title'
            className='w-100 form-control'
            rows={2}
            ref={titleRef}
            onChange={inputEvent}
            defaultValue={isDueDatePassed ? truncatedCommentText : task?.Name}
            placeholder='Enter title text.'
            maxLength={120}
            autoFocus
            onFocus={handleFocus}
            style={{ minHeight: '70px', resize: 'vertical', overflow: 'auto' }}
          />
          {isReminderMessageEmpty && (
            <p className='text-red reminder-title-warning m-0 mt-1' style={{ fontSize: '12px' }}>
              {quote.REMINDER_TITLE_EMPTY}
            </p>
          )}
        </div>

        {isDueDatePassed ? (
          <div className='row'>
            {/* Past Due Date - Date Options Row */}
            {/* Choose Date */}
            <div className='col-md-4 form-group'>
              <label className='d-flex align-items-center mb-1'>
                <input
                  id='dynamic-reminder-popup-choose-date-checkbox'
                  className='mr-2'
                  type='checkbox'
                  checked={chooseDateChecked}
                  onChange={handleChooseDateChecked}
                />
                <span>{label.CHOOSE_DATE}</span>
              </label>
              <div className='d-flex align-items-center'>
                <DatePicker
                  size='small'
                  className='reminder-date-picker'
                  value={selectedDate}
                  onChange={handleDateChange}
                  disabled={!chooseDateChecked}
                  min={today}
                  format='dd-MMM-yyyy'
                />
              </div>
            </div>

            {/* Days from today */}
            <div className='col-md-4 form-group'>
              <label className='d-flex align-items-center mb-1'>
                <input
                  id='dynamic-reminder-popup-days-after-checkbox'
                  className='mr-2'
                  type='checkbox'
                  checked={daysAfterChecked}
                  onChange={handleDaysAfterChecked}
                />
                <span>{label.DAYS_FROM_TODAY}</span>
              </label>
              <div className='d-flex align-items-center'>
                <input
                  className='form-control days-from-today-input'
                  type='numeric'
                  value={daysAfter}
                  disabled={!daysAfterChecked}
                  onChange={handleNumberOfDaysAfter}
                  onKeyDown={handleKeyDown}
                  maxLength={number.TWO}
                />
                <div className='dynamic-reminder-popup-inc-dec-btn-group d-flex flex-column ml-1'>
                  <button
                    id='dynamic-reminder-popup-inc-days-after'
                    className='dynamic-reminder-popup-inc-dec-btn d-flex align-items-center justify-content-center border-0 outline-none'
                    disabled={!daysAfterChecked}
                    onClick={() => handleDaysAfterChange(true)}>
                    {icon.ARROW_UP}
                  </button>
                  <button
                    id='dynamic-reminder-popup-dec-days-after'
                    className='dynamic-reminder-popup-inc-dec-btn d-flex align-items-center justify-content-center border-0 outline-none'
                    disabled={!daysAfterChecked}
                    onClick={() => handleDaysAfterChange(false)}>
                    {icon.ARROW_DOWN}
                  </button>
                </div>
              </div>
            </div>

            {/* At (Time) */}
            <div className='col-md-4 form-group'>
              <label className='d-flex align-items-center mb-1'>
                <span>{label.AT}</span>
              </label>
              <div className='d-flex align-items-center'>
                <TimePicker
                title=''
                popupSettings={{ className: "reminder-timepicker-popup", appendTo: timePickerPopupHostRef.current }}
                onOpen={removeReminderTimePickerActionTooltips}
                  size='small'
                  className='reminder-time-picker'
                  steps={{ minute: 10 }}
                  value={selectedTime}
                  onChange={handleTimeChange}
                  disabled={!chooseDateChecked && !daysAfterChecked}
                />
                <div ref={timePickerPopupHostRef} />
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Future Due Date - Date Options Row */}
            <div className='d-flex align-items-end reminder-options-row'>
              {/* Choose Date */}
              <div className='reminder-option-col' style={{ flex: 1 }}>
                <label className='d-flex align-items-center mb-1'>
                  <input
                    id='dynamic-reminder-popup-choose-date-checkbox-future'
                    className='mr-2'
                    type='checkbox'
                    checked={chooseDateChecked}
                    onChange={handleChooseDateChecked}
                  />
                  <span>{label.CHOOSE_DATE}</span>
                </label>
                <div className='d-flex align-items-center'>
                  <DatePicker
                    size='small'
                    className='reminder-date-picker'
                    value={selectedDate}
                    onChange={handleDateChange}
                    disabled={!chooseDateChecked}
                    min={today}
                    format='dd-MMM-yyyy'
                  />
                </div>
              </div>

              {/* Days before due date */}
              <div className='reminder-option-col mx-2' style={{ flex: 1 }}>
                <label className='d-flex align-items-center mb-1'>
                  <input
                    id='dynamic-reminder-popup-days-before-checkbox'
                    className='mr-2'
                    type='checkbox'
                    checked={daysBeforeChecked}
                    onChange={handleDaysBeforeChecked}
                  />
                  <span>{label.DAYS_BEFORE_DUE_DATE}</span>
                </label>
                <div className='d-flex align-items-center'>
                  <input
                    className='form-control days-from-today-input'
                    type='numeric'
                    value={daysBefore}
                    disabled={new Date(task.DueDate) == today || !daysBeforeChecked}
                    onChange={handleNumberOfDaysBefore}
                    onKeyDown={handleKeyDown}
                    maxLength={number.TWO}
                  />
                  <div className='dynamic-reminder-popup-inc-dec-btn-group d-flex flex-column ml-1'>
                    <button
                      id='dynamic-reminder-popup-inc-days-before'
                      className='dynamic-reminder-popup-inc-dec-btn d-flex align-items-center justify-content-center border-0 outline-none'
                      disabled={!daysBeforeChecked}
                      onClick={() => handleDaysBeforeChange(true)}>
                      {icon.ARROW_UP}
                    </button>
                    <button
                      id='dynamic-reminder-popup-dec-days-before'
                      className='dynamic-reminder-popup-inc-dec-btn d-flex align-items-center justify-content-center border-0 outline-none'
                      disabled={!daysBeforeChecked}
                      onClick={() => handleDaysBeforeChange(false)}>
                      {icon.ARROW_DOWN}
                    </button>
                  </div>
                </div>
                {displayWarning && (
                  <p className='text-red m-0 mt-1' style={{ fontSize: '11px' }}>{quote.REMINDER_INVALID_DATE}</p>
                )}
              </div>

              {/* At (Time) */}
              <div className='reminder-option-col' style={{ flex: 1 }}>
                <label className='d-flex align-items-center mb-1'>
                  <span>{label.AT}</span>
                </label>
                <div className='d-flex align-items-center'>
                  <TimePicker
                    title=''
                    popupSettings={{ className: "reminder-timepicker-popup", appendTo: timePickerPopupHostRef.current }}
                    onOpen={removeReminderTimePickerActionTooltips}
                    size='small'
                    className='reminder-time-picker'
                    steps={{ minute: 10 }}
                    value={selectedTime}
                    onChange={handleTimeChange}
                    disabled={!chooseDateChecked && !daysBeforeChecked}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className='dt-popup-footer text-right w-100 px-0'>
          <button
            id='dynamic-reminder-popup-reminder-cancel-btn'
            className='btn btn-ghost mr-2 reminder-confirmation-btn'
            onClick={handleCancel}>
            {button.CANCEL}
          </button>
          <button
            id='dynamic-reminder-popup-reminder-save-btn'
            className='btn btn-primary reminder-confirmation-btn'
            disabled={
              isReminderMessageEmpty ||
              (isDueDatePassed
                ? !chooseDateChecked && !daysAfterChecked
                : !chooseDateChecked && !daysBeforeChecked)
            }
            onClick={handleSaveReminder}>
            {button.SAVE}
          </button>
        </div>
        </Tooltip>
      </div>
    );
  };

  return { popupBodyContent, handleSaveReminder, closeCustomPopup, handleKeyDown, getUpdatedCommonGridReminders, createReminder, handleCancel };
};
